import React, { useState, useEffect } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Select, MenuItem, FormControl, InputLabel, IconButton, Tooltip, Divider,
  Stepper, Step, StepLabel, Switch, FormControlLabel, Alert, useTheme
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import hipersendService from '../../services/hipersend'

const sf = hipersendService.smartFilter

// ===========================================================================
// Construtor avançado (campo a campo) — mantido 100% personalizável
// ===========================================================================
const FIELDS = [
  { key: 'intent', label: 'Tipo da mensagem', type: 'FACT', path: 'facts.intent', valueType: 'intent' },
  { key: 'dueDateIso', label: 'Data de vencimento', type: 'FACT', path: 'facts.dates', valueType: 'date' },
  { key: 'daysToDue', label: 'Dias até o vencimento', type: 'FACT', path: 'facts.daysToDueDate', valueType: 'number' },
  { key: 'amount', label: 'Valor em R$', type: 'FACT', path: 'facts.amounts.0', valueType: 'number' },
  { key: 'hasPix', label: 'Tem PIX', type: 'FACT', path: 'facts.hasPix', valueType: 'boolean' },
  { key: 'hasBoleto', label: 'Tem boleto', type: 'FACT', path: 'facts.hasBoleto', valueType: 'boolean' },
  { key: 'unresolvedVar', label: 'Variável não preenchida (#var#)', type: 'FACT', path: 'facts.hasUnresolvedVariable', valueType: 'boolean' },
  { key: 'message', label: 'Texto da mensagem', type: 'FIELD', path: 'rawMessage', valueType: 'text' },
  { key: 'phone', label: 'Telefone', type: 'FIELD', path: 'normalizedPhone', valueType: 'text' },
  { key: 'localHour', label: 'Hora local (0-23)', type: 'TIME_WINDOW', path: 'context.localHour', valueType: 'number' },
  { key: 'weekday', label: 'Dia da semana (0=dom..6=sáb)', type: 'FIELD', path: 'context.weekday', valueType: 'number' },
  { key: 'dupWindow', label: 'Duplicadas na janela', type: 'COUNTER', path: 'counters.fingerprintHits10min', valueType: 'number' },
  { key: 'dailyToRecipient', label: 'Enviadas hoje p/ o número', type: 'COUNTER', path: 'counters.dailyToRecipient', valueType: 'number' },
  { key: 'hoursSinceLast', label: 'Horas desde a última', type: 'COUNTER', path: 'counters.hoursSinceLastToRecipient', valueType: 'number' },
  { key: 'overdueMonths', label: 'Meses de atraso do cliente', type: 'COUNTER', path: 'counters.recipientOverdueMonths', valueType: 'number' },
  { key: 'suppression', label: 'Está na lista de bloqueio', type: 'RECIPIENT_LIST', path: 'normalizedPhone', valueType: 'list', listKey: 'suppressionList' },
  { key: 'vip', label: 'Está na lista de sempre enviar', type: 'RECIPIENT_LIST', path: 'normalizedPhone', valueType: 'list', listKey: 'vipList' }
]

const OP_LABELS = {
  eq: 'é igual a', ne: 'é diferente de',
  gt: 'maior que', gte: 'maior ou igual a', lt: 'menor que', lte: 'menor ou igual a',
  contains: 'contém', notContains: 'não contém', regex: 'casa com (regex)',
  between: 'está entre', notBetween: 'não está entre',
  exists: 'existe / tem valor', notExists: 'não existe / vazio', in: 'está na lista'
}

const OPS_BY_TYPE = {
  intent: ['eq', 'ne', 'in'],
  date: ['contains', 'notContains', 'eq', 'ne', 'exists', 'notExists'],
  number: ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between', 'notBetween', 'exists', 'notExists'],
  boolean: ['eq'],
  text: ['contains', 'notContains', 'eq', 'ne', 'regex', 'exists', 'notExists'],
  list: ['in']
}

const INTENTS = ['cobranca', 'atraso', 'desbloqueio', 'marketing', 'aviso', 'confirmacao', 'pesquisa', 'boasvindas', 'unknown']

const ACTIONS = [
  { value: 'ALLOW', label: 'Permitir (enviar)' },
  { value: 'BLOCK', label: 'Bloquear' },
  { value: 'DELAY', label: 'Atrasar p/ horário comercial' },
  { value: 'REQUIRE_APPROVAL', label: 'Exigir aprovação' },
  { value: 'TAG_ONLY', label: 'Só marcar (não interfere)' }
]

const NO_VALUE_OPS = ['exists', 'notExists']
const RANGE_OPS = ['between', 'notBetween']

function emptyItem() {
  return { fieldKey: 'intent', operator: 'eq', value: '', value2: '' }
}
function fieldByKey(key) {
  return FIELDS.find((f) => f.key === key) || FIELDS[0]
}
function itemFromCondition(c) {
  const field = FIELDS.find((f) => f.type === c.type && f.path === c.path && (f.valueType !== 'list' || f.listKey === c.value)) || FIELDS[0]
  const isRange = RANGE_OPS.includes(c.operator)
  return {
    fieldKey: field.key,
    operator: c.operator,
    value: isRange ? (Array.isArray(c.value) ? c.value[0] : '') : (c.value === undefined ? '' : String(c.value)),
    value2: isRange && Array.isArray(c.value) ? c.value[1] : ''
  }
}

// ===========================================================================
// Objetivos guiados (wizard) — cada um monta uma regra parametrizada
// ===========================================================================
function isoToBr(s) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(s || '')) ? String(s).split('-').reverse().join('/') : (s || '')
}

const OBJECTIVES = [
  { key: 'due-date-allow', icon: '📅', title: 'Liberar só um vencimento', blurb: 'Passa só mensagens com a data que você escolher.',
    build: (p) => ({ suggestedName: `Liberar só vencimento ${isoToBr(p.date)}`, priority: 500,
      conditions: { op: 'AND', items: [{ type: 'FACT', path: 'facts.dates', operator: 'contains', value: p.date }] },
      action: { type: 'ALLOW', params: { reason: 'specific_due_date' } } }) },
  { key: 'only-overdue', icon: '📛', title: 'Só faturas já vencidas', blurb: 'Passa só cobrança que já passou do vencimento.',
    build: () => ({ suggestedName: 'Só faturas já vencidas', priority: 540,
      conditions: { op: 'AND', items: [{ type: 'FACT', path: 'facts.daysToDueDate', operator: 'lt', value: 0 }] },
      action: { type: 'ALLOW', params: { reason: 'overdue_only' } } }) },
  { key: 'block-list', icon: '🚫', title: 'Bloquear números da minha lista', blurb: 'Descarta tudo para os números da lista de bloqueio.',
    build: () => ({ suggestedName: 'Bloquear lista de bloqueio', priority: 990,
      conditions: { op: 'AND', items: [{ type: 'RECIPIENT_LIST', path: 'normalizedPhone', operator: 'in', value: 'suppressionList' }] },
      action: { type: 'BLOCK', params: { reason: 'suppressed_recipient' } } }) },
  { key: 'no-early', icon: '⏰', title: 'Não cobrar antes de vencer', blurb: 'Bloqueia cobrança enviada cedo demais.',
    build: (p) => ({ suggestedName: `Não cobrar mais de ${p.n} dias antes`, priority: 600,
      conditions: { op: 'AND', items: [
        { type: 'FACT', path: 'facts.intent', operator: 'eq', value: 'cobranca' },
        { type: 'FACT', path: 'facts.daysToDueDate', operator: 'gt', value: Number(p.n) }] },
      action: { type: 'BLOCK', params: { reason: 'billing_too_early' } } }) },
  { key: 'high-value', icon: '💰', title: 'Cobrança alta pede aprovação', blurb: 'Acima do valor, fica pendente pra você aprovar.',
    build: (p) => ({ suggestedName: `Cobrança acima de R$ ${p.n} pede aprovação`, priority: 700,
      conditions: { op: 'AND', items: [
        { type: 'FACT', path: 'facts.intent', operator: 'eq', value: 'cobranca' },
        { type: 'FACT', path: 'facts.amounts.0', operator: 'gte', value: Number(p.n) }] },
      action: { type: 'REQUIRE_APPROVAL', params: { reason: 'high_value_billing' } } }) },
  { key: 'unresolved', icon: '🧩', title: 'Bloquear variável não preenchida', blurb: 'Barra mensagem com #var#, %var% ou {var} literal.',
    build: () => ({ suggestedName: 'Bloquear variável não preenchida', priority: 1100,
      conditions: { op: 'AND', items: [{ type: 'FACT', path: 'facts.hasUnresolvedVariable', operator: 'eq', value: true }] },
      action: { type: 'BLOCK', params: { reason: 'unresolved_variable' } } }) },
  { key: 'dedup', icon: '🔁', title: 'Não repetir a mesma mensagem', blurb: 'Bloqueia mensagem idêntica na janela de tempo.',
    build: () => ({ suggestedName: 'Não repetir a mesma mensagem', priority: 1000,
      conditions: { op: 'AND', items: [{ type: 'COUNTER', path: 'counters.fingerprintHits10min', operator: 'gte', value: 1 }] },
      action: { type: 'BLOCK', params: { reason: 'duplicate_within_window' } } }) },
  { key: 'outside-hours', icon: '🌙', title: 'Segurar fora do horário', blurb: 'Atrasa mensagens fora do intervalo comercial.',
    build: (p) => ({ suggestedName: `Segurar fora de ${p.from}h–${p.to}h`, priority: 400,
      conditions: { op: 'OR', items: [
        { type: 'TIME_WINDOW', path: 'context.localHour', operator: 'lt', value: Number(p.from) },
        { type: 'TIME_WINDOW', path: 'context.localHour', operator: 'gte', value: Number(p.to) }] },
      action: { type: 'DELAY', params: { reason: 'outside_business_hours' } } }) },
  { key: 'old-overdue', icon: '🔕', title: 'Parar de cobrar inadimplência antiga', blurb: 'Silencia tudo p/ quem deve há mais de N meses (título mais antigo).',
    build: (p) => ({ suggestedName: `Parar de cobrar atraso de +${p.months} meses`, priority: 650,
      conditions: { op: 'AND', items: [{ type: 'COUNTER', path: 'counters.recipientOverdueMonths', operator: 'gte', value: Number(p.months) }] },
      action: { type: 'BLOCK', params: { reason: 'inadimplencia_antiga' } } }) },
  { key: 'scratch', icon: '⚙️', title: 'Começar do zero (avançado)', blurb: 'Monte campo a campo, com total liberdade.', advanced: true }
]

function initParams(key) {
  if (key === 'due-date-allow') return { date: '' }
  if (key === 'no-early') return { n: 3 }
  if (key === 'high-value') return { n: 500 }
  if (key === 'outside-hours') return { from: 8, to: 20 }
  if (key === 'old-overdue') return { months: 6 }
  return {}
}

// ===========================================================================
// Prévia em linguagem natural (fonte única: as condições/ação reais)
// ===========================================================================
const FIELD_NL = {
  'facts.intent': 'o tipo da mensagem', 'facts.dates': 'a data de vencimento',
  'facts.daysToDueDate': 'os dias até o vencimento', 'facts.amounts.0': 'o valor',
  'rawMessage': 'o texto da mensagem', 'normalizedPhone': 'o telefone',
  'context.localHour': 'a hora local', 'context.weekday': 'o dia da semana',
  'counters.fingerprintHits10min': 'as mensagens duplicadas na janela',
  'counters.dailyToRecipient': 'as mensagens enviadas hoje ao número',
  'counters.hoursSinceLastToRecipient': 'as horas desde a última ao número',
  'counters.recipientOverdueMonths': 'os meses de atraso do cliente'
}
const OP_NL = {
  eq: 'é', ne: 'não é', gt: 'é maior que', gte: 'é maior ou igual a',
  lt: 'é menor que', lte: 'é menor ou igual a', contains: 'contém', notContains: 'não contém',
  regex: 'casa com', between: 'está entre', notBetween: 'não está entre'
}
const BOOL_NL = {
  'facts.hasUnresolvedVariable': ['tem variável não preenchida', 'não tem variável não preenchida'],
  'facts.hasPix': ['tem PIX', 'não tem PIX'],
  'facts.hasBoleto': ['tem boleto', 'não tem boleto']
}
const WEEKDAYS = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado']
const ACTION_NL = {
  ALLOW: 'permitir o envio', BLOCK: 'bloquear o envio',
  DELAY: 'atrasar para o horário comercial', REQUIRE_APPROVAL: 'enviar para aprovação manual',
  TAG_ONLY: 'apenas marcar (não interfere)'
}

function fmtValue(path, value) {
  if (Array.isArray(value)) return value.map((v) => fmtValue(path, v)).join(' e ')
  if (path === 'facts.dates' && /^\d{4}-\d{2}-\d{2}$/.test(String(value))) return isoToBr(value)
  if (path === 'context.weekday' && WEEKDAYS[value] !== undefined) return WEEKDAYS[value]
  if (path === 'facts.amounts.0') return `R$ ${value}`
  if (path === 'context.localHour') return `${value}h`
  return String(value)
}
function describeCondition(item) {
  if (item.type === 'RECIPIENT_LIST') {
    if (item.value === 'suppressionList') return 'o número está na lista de bloqueio'
    if (item.value === 'vipList') return 'o número está na lista de sempre enviar'
    return 'o número está na lista'
  }
  if (BOOL_NL[item.path] && item.operator === 'eq') {
    return BOOL_NL[item.path][(item.value === true || item.value === 'true') ? 0 : 1]
  }
  const subj = FIELD_NL[item.path] || item.path
  if (item.operator === 'exists') return `${subj} existe`
  if (item.operator === 'notExists') return `${subj} está vazio`
  const op = OP_NL[item.operator] || item.operator
  return `${subj} ${op} ${fmtValue(item.path, item.value)}`.trim()
}
function describeRule(conditions, action) {
  const items = (conditions && conditions.items) || []
  const joiner = conditions && conditions.op === 'OR' ? ' OU ' : ' E '
  const when = items.length ? items.map(describeCondition).join(joiner) : 'qualquer mensagem'
  const then = ACTION_NL[action && action.type] || (action && action.type) || '—'
  return { when, then }
}

// ===========================================================================
export default function RuleEditor({ open, onClose, onSaved, api, secret, rule }) {
  const editing = !!rule
  const theme = useTheme()

  const [step, setStep] = useState(0)
  const [objective, setObjective] = useState(null)
  const [params, setParams] = useState({})

  // construtor avançado / edição
  const [name, setName] = useState('')
  const [source, setSource] = useState('any')
  const [priority, setPriority] = useState(100)
  const [op, setOp] = useState('AND')
  const [items, setItems] = useState([emptyItem()])
  const [actionType, setActionType] = useState('ALLOW')
  const [reason, setReason] = useState('')

  const [enabledOnCreate, setEnabledOnCreate] = useState(true)
  const [filterMode, setFilterMode] = useState('blacklist')
  const [enablingWl, setEnablingWl] = useState(false)
  const [wlConfirming, setWlConfirming] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const advanced = editing || objective === 'scratch'
  const reviewStep = editing ? 1 : 2

  useEffect(() => {
    if (!open) return
    setError('')
    setStep(0)
    setParams({})
    setEnablingWl(false)
    setWlConfirming(false)
    if (rule) {
      setObjective(null)
      setName(rule.name || '')
      setSource(rule.source || 'any')
      setPriority(rule.priority != null ? rule.priority : 100)
      setOp(rule.conditions?.op || 'AND')
      const its = (rule.conditions?.items || []).map(itemFromCondition)
      setItems(its.length ? its : [emptyItem()])
      setActionType(rule.action?.type || 'ALLOW')
      setReason(rule.action?.params?.reason || '')
      setEnabledOnCreate(!!rule.enabled)
    } else {
      setObjective(null)
      setName(''); setSource('any'); setPriority(100); setOp('AND')
      setItems([emptyItem()]); setActionType('ALLOW'); setReason('')
      setEnabledOnCreate(true)
    }
    // descobre a estratégia atual p/ avisar sobre lista-branca
    sf.getSettings(api, secret).then((s) => setFilterMode(s?.filterMode || 'blacklist')).catch(() => {})
  }, [open, rule, api, secret])

  const updateItem = (idx, patch) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)))
  const onFieldChange = (idx, fieldKey) => {
    const allowed = OPS_BY_TYPE[fieldByKey(fieldKey).valueType]
    updateItem(idx, { fieldKey, operator: allowed[0], value: '', value2: '' })
  }
  const coerce = (valueType, raw) => {
    if (valueType === 'number') { const n = Number(raw); return Number.isNaN(n) ? raw : n }
    if (valueType === 'boolean') return raw === 'true' || raw === true
    return raw
  }
  const buildConditions = () => {
    const built = items.map((it) => {
      const field = fieldByKey(it.fieldKey)
      const cond = { type: field.type, path: field.path, operator: it.operator }
      if (field.valueType === 'list') { cond.value = field.listKey || 'suppressionList'; return cond }
      if (NO_VALUE_OPS.includes(it.operator)) { cond.value = true; return cond }
      if (RANGE_OPS.includes(it.operator)) { cond.value = [coerce('number', it.value), coerce('number', it.value2)]; return cond }
      if (it.operator === 'in') { cond.value = String(it.value).split(',').map((s) => s.trim()).filter(Boolean); return cond }
      cond.value = coerce(field.valueType, it.value)
      return cond
    })
    return { op, items: built }
  }

  const computeDraft = () => {
    if (advanced) {
      return { name: name.trim(), source, priority: Number(priority) || 100,
        conditions: buildConditions(), action: { type: actionType, params: reason ? { reason } : {} } }
    }
    const obj = OBJECTIVES.find((o) => o.key === objective)
    const built = obj && obj.build ? obj.build(params) : { conditions: { op: 'AND', items: [] }, action: { type: 'ALLOW', params: {} }, suggestedName: '', priority: 100 }
    return { name: name.trim() || built.suggestedName, source, priority: built.priority || 100,
      conditions: built.conditions, action: built.action }
  }

  const pickObjective = (key) => {
    setObjective(key)
    setParams(initParams(key))
    setName('')
  }

  // validações p/ habilitar "Próximo"
  const canAdvance = () => {
    if (!editing && step === 0) return !!objective
    if (!advanced && step === 1) {
      if (objective === 'due-date-allow') return !!params.date
      if (objective === 'no-early' || objective === 'high-value') return params.n !== '' && params.n != null
      if (objective === 'outside-hours') return params.from !== '' && params.to !== ''
      if (objective === 'old-overdue') return params.months !== '' && params.months != null
      return true
    }
    return true
  }

  const goNext = () => {
    const next = step + 1
    if (next === reviewStep && !advanced && objective && !name.trim()) {
      try { setName((OBJECTIVES.find((o) => o.key === objective).build(params)).suggestedName || '') } catch (e) { /* noop */ }
    }
    setStep(next)
  }

  const enableWhitelist = async () => {
    setEnablingWl(true)
    try { await sf.updateSettings(api, secret, { filterMode: 'whitelist' }); setFilterMode('whitelist'); setWlConfirming(false) }
    catch (e) { /* noop */ }
    finally { setEnablingWl(false) }
  }

  const save = async () => {
    const draft = computeDraft()
    if (!draft.name) { setError('Dê um nome à regra'); return }
    setSaving(true); setError('')
    try {
      const payload = {
        name: draft.name, source: draft.source, priority: draft.priority,
        enabled: editing ? rule.enabled : enabledOnCreate,
        conditions: draft.conditions, action: draft.action
      }
      if (editing) await sf.updateRule(api, secret, rule._id, payload)
      else await sf.createRule(api, secret, payload)
      onSaved()
      onClose()
    } catch (e) { setError('Falha ao salvar a regra') }
    finally { setSaving(false) }
  }

  // ---- render helpers ----
  const stepLabels = editing ? ['Montar regra', 'Revisão'] : ['Objetivo', advanced ? 'Montar regra' : 'Detalhes', 'Revisão']

  const draft = step === reviewStep ? computeDraft() : null
  const nl = draft ? describeRule(draft.conditions, draft.action) : null
  const allowNeedsWhitelist = draft && draft.action?.type === 'ALLOW' && filterMode !== 'whitelist'

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '18px' } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>{editing ? 'Editar regra' : 'Nova regra'}</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} className="mb-5 mt-1">
          {stepLabels.map((l) => <Step key={l}><StepLabel>{l}</StepLabel></Step>)}
        </Stepper>

        {/* PASSO: OBJETIVO (apenas nova regra) */}
        {!editing && step === 0 && (
          <div>
            <div className="text-sm opacity-70 mb-3">O que você quer fazer? Escolha um objetivo — o resto vem pré-preenchido.</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {OBJECTIVES.map((o) => {
                const sel = objective === o.key
                return (
                  <div
                    key={o.key}
                    onClick={() => pickObjective(o.key)}
                    className="cursor-pointer rounded-xl p-3 transition"
                    style={{
                      border: `1.5px ${o.advanced ? 'dashed' : 'solid'} ${sel ? theme.palette.primary.main : theme.palette.divider}`,
                      background: sel ? alpha(theme.palette.primary.main, 0.08) : theme.palette.background.paper,
                      boxShadow: sel ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}` : 'none'
                    }}
                  >
                    <div style={{ fontSize: '1.15rem' }}>{o.icon}</div>
                    <div className="text-sm font-semibold mt-1" style={{ lineHeight: 1.2 }}>{o.title}</div>
                    <div className="text-xs opacity-60 mt-1" style={{ lineHeight: 1.25 }}>{o.blurb}</div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* PASSO: DETALHES (guiado) */}
        {!editing && !advanced && step === 1 && (
          <div className="space-y-3 pt-1 max-w-md">
            <div className="text-sm opacity-70">Só os detalhes desse objetivo:</div>
            {objective === 'due-date-allow' && (
              <TextField
                fullWidth size="small" type="date" label="Data de vencimento que pode passar"
                InputLabelProps={{ shrink: true }} value={params.date || ''}
                onChange={(e) => setParams({ ...params, date: e.target.value })}
              />
            )}
            {objective === 'no-early' && (
              <TextField fullWidth size="small" type="number" label="Bloquear se faltar mais de (dias)"
                value={params.n} onChange={(e) => setParams({ ...params, n: e.target.value })} />
            )}
            {objective === 'high-value' && (
              <TextField fullWidth size="small" type="number" label="Valor mínimo para conferir (R$)"
                value={params.n} onChange={(e) => setParams({ ...params, n: e.target.value })} />
            )}
            {objective === 'outside-hours' && (
              <div className="flex gap-3">
                <TextField size="small" type="number" label="Das (hora)" value={params.from} onChange={(e) => setParams({ ...params, from: e.target.value })} />
                <TextField size="small" type="number" label="Até (hora)" value={params.to} onChange={(e) => setParams({ ...params, to: e.target.value })} />
              </div>
            )}
            {objective === 'block-list' && (
              <Alert severity="info">Essa regra usa a aba <b>Lista de bloqueio</b>. Importe lá os números (ex.: contratos comodato/bloqueados) e eles serão descartados.</Alert>
            )}
            {objective === 'old-overdue' && (
              <TextField fullWidth size="small" type="number" label="Bloquear a partir de (meses de atraso)"
                value={params.months} onChange={(e) => setParams({ ...params, months: e.target.value })}
                helperText="Olha o título em aberto mais antigo do cliente. Desbloqueio/reativação continuam passando." />
            )}
            {['only-overdue', 'unresolved', 'dedup'].includes(objective) && (
              <Alert severity="info">Sem parâmetros — pode seguir para a revisão.</Alert>
            )}
          </div>
        )}

        {/* PASSO: CONSTRUTOR AVANÇADO (do zero / edição) */}
        {((!editing && advanced && step === 1) || (editing && step === 0)) && (
          <div className="space-y-4 pt-1">
            <TextField fullWidth size="small" label="Nome da regra" value={name} onChange={(e) => setName(e.target.value)} />
            <div className="flex gap-3">
              <FormControl size="small" className="flex-1">
                <InputLabel>Origem</InputLabel>
                <Select label="Origem" value={source} onChange={(e) => setSource(e.target.value)}>
                  {['any', 'ixc', 'sgp', 'mkauth', 'atlaz', 'generic'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField size="small" type="number" label="Prioridade" className="w-40" value={priority} onChange={(e) => setPriority(e.target.value)} />
            </div>

            <Divider textAlign="left" className="text-xs">QUANDO ({op === 'AND' ? 'todas' : 'qualquer uma'})</Divider>
            <FormControl size="small" className="w-48">
              <InputLabel>Conector</InputLabel>
              <Select label="Conector" value={op} onChange={(e) => setOp(e.target.value)}>
                <MenuItem value="AND">E (todas as condições)</MenuItem>
                <MenuItem value="OR">OU (qualquer condição)</MenuItem>
              </Select>
            </FormControl>

            {items.map((it, idx) => {
              const field = fieldByKey(it.fieldKey)
              const ops = OPS_BY_TYPE[field.valueType]
              const showValue = field.valueType !== 'list' && !NO_VALUE_OPS.includes(it.operator)
              const isRange = RANGE_OPS.includes(it.operator)
              return (
                <div key={idx} className="flex gap-2 items-center flex-wrap">
                  <FormControl size="small" className="flex-1 min-w-[200px]">
                    <InputLabel>Campo</InputLabel>
                    <Select label="Campo" value={it.fieldKey} onChange={(e) => onFieldChange(idx, e.target.value)}>
                      {FIELDS.map((f) => <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" className="min-w-[160px]">
                    <InputLabel>Condição</InputLabel>
                    <Select label="Condição" value={it.operator} onChange={(e) => updateItem(idx, { operator: e.target.value })}>
                      {ops.map((o) => <MenuItem key={o} value={o}>{OP_LABELS[o]}</MenuItem>)}
                    </Select>
                  </FormControl>
                  {showValue && field.valueType === 'boolean' && (
                    <FormControl size="small" className="min-w-[120px]">
                      <InputLabel>Valor</InputLabel>
                      <Select label="Valor" value={String(it.value || 'true')} onChange={(e) => updateItem(idx, { value: e.target.value })}>
                        <MenuItem value="true">Sim</MenuItem>
                        <MenuItem value="false">Não</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                  {showValue && field.valueType === 'intent' && it.operator !== 'in' && (
                    <FormControl size="small" className="min-w-[160px]">
                      <InputLabel>Valor</InputLabel>
                      <Select label="Valor" value={it.value || 'cobranca'} onChange={(e) => updateItem(idx, { value: e.target.value })}>
                        {INTENTS.map((i) => <MenuItem key={i} value={i}>{i}</MenuItem>)}
                      </Select>
                    </FormControl>
                  )}
                  {showValue && field.valueType === 'date' && (
                    <TextField size="small" type="date" className="min-w-[160px]" InputLabelProps={{ shrink: true }} label={isRange ? 'De' : 'Valor'} value={it.value} onChange={(e) => updateItem(idx, { value: e.target.value })} />
                  )}
                  {showValue && !['boolean', 'date'].includes(field.valueType) && !(field.valueType === 'intent' && it.operator !== 'in') && (
                    <TextField size="small" className="min-w-[150px]" label={isRange ? 'De' : 'Valor'} value={it.value} onChange={(e) => updateItem(idx, { value: e.target.value })} />
                  )}
                  {showValue && isRange && (
                    <TextField size="small" className="min-w-[120px]" label="Até" value={it.value2} onChange={(e) => updateItem(idx, { value2: e.target.value })} />
                  )}
                  <Tooltip title="Remover condição">
                    <span>
                      <IconButton size="small" disabled={items.length === 1} onClick={() => setItems(items.filter((_, i) => i !== idx))}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
              )
            })}
            <Button size="small" startIcon={<AddIcon />} onClick={() => setItems([...items, emptyItem()])}>Adicionar condição</Button>

            <Divider textAlign="left" className="text-xs">ENTÃO</Divider>
            <FormControl size="small" fullWidth>
              <InputLabel>Ação</InputLabel>
              <Select label="Ação" value={actionType} onChange={(e) => setActionType(e.target.value)}>
                {ACTIONS.map((a) => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
              </Select>
            </FormControl>
          </div>
        )}

        {/* PASSO: REVISÃO */}
        {step === reviewStep && draft && (
          <div className="space-y-4 pt-1">
            <TextField fullWidth size="small" label="Nome da regra" value={name} onChange={(e) => setName(e.target.value)} />

            <div className="rounded-xl p-4" style={{ background: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.06), border: `1px solid ${theme.palette.divider}` }}>
              <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: theme.palette.text.secondary }}>👁️ Resumo da regra</div>
              <div className="text-[15px]" style={{ color: theme.palette.text.primary }}>
                <b style={{ color: theme.palette.primary.main }}>QUANDO</b> {nl.when} &nbsp;→&nbsp; <b style={{ color: theme.palette.primary.main }}>ENTÃO</b> {nl.then}.
              </div>
            </div>

            {allowNeedsWhitelist && !wlConfirming && (
              <Alert
                severity="warning"
                action={<Button color="inherit" size="small" onClick={() => setWlConfirming(true)}>Ativar lista-branca</Button>}
              >
                Regras de <b>permitir</b> só fazem efeito na estratégia <b>lista-branca</b> ("enviar só o que as regras liberarem"). Hoje está em "{filterMode === 'whitelist' ? 'lista-branca' : 'passar tudo'}".
              </Alert>
            )}
            {allowNeedsWhitelist && wlConfirming && (
              <Alert severity="error">
                <div className="mb-2">
                  Atenção: isso muda a estratégia da <b>instância inteira</b>. Na lista-branca, <b>tudo passa a ser bloqueado por padrão</b> — só sai o que alguma regra de <b>permitir</b> liberar (esta regra, a lista de sempre enviar, etc.). Confirma?
                </div>
                <div className="flex gap-2">
                  <Button color="error" variant="contained" size="small" disabled={enablingWl} onClick={enableWhitelist}>
                    {enablingWl ? 'Ativando…' : 'Sim, bloquear o resto'}
                  </Button>
                  <Button color="inherit" size="small" disabled={enablingWl} onClick={() => setWlConfirming(false)}>Cancelar</Button>
                </div>
              </Alert>
            )}
            {draft.action?.type === 'ALLOW' && filterMode === 'whitelist' && (
              <Alert severity="success">Estratégia lista-branca ativa — esta regra de "permitir" terá efeito.</Alert>
            )}

            {!editing && (
              <FormControlLabel
                control={<Switch checked={enabledOnCreate} onChange={(e) => setEnabledOnCreate(e.target.checked)} />}
                label="Ativar esta regra agora"
              />
            )}

            {error && <div className="text-red-600 text-sm">{error}</div>}
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        {step > 0 && <Button onClick={() => setStep(step - 1)}>Voltar</Button>}
        {step < reviewStep && <Button variant="contained" disabled={!canAdvance()} onClick={goNext}>Próximo</Button>}
        {step === reviewStep && <Button variant="contained" onClick={save} disabled={saving}>{editing ? 'Salvar' : 'Criar regra'}</Button>}
      </DialogActions>
    </Dialog>
  )
}
